'use client'
import type { ChangeEvent, FC } from 'react'
import { forwardRef, useCallback } from 'react'
import type { NumberFormatProps, NumberFormatValues } from 'react-number-format'
import NumberFormat from 'react-number-format'

import {
  Input,
  persianNumbersToEnglish,
  type InputProps,
} from '@repo/ui/components'

interface InputNumberProps extends NumberFormatProps {
  maskCharacter?: string
  separatorCharacter?: string
  thousandSeparator?: boolean | string
}

export type { InputNumberProps }

const CustomTextField = (props: InputProps) => {
  return (
    <Input
      {...props}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        e.target.value = persianNumbersToEnglish(e.target.value)
        props.onChange?.(e)
      }}
      className='text-center'
      autoComplete='off'
    />
  )
}

export const InputNumber: FC<InputNumberProps> = forwardRef<
  HTMLInputElement,
  InputNumberProps
>(function BankCardField(
  { onValueChange, label = '', thousandSeparator = false, ...rest },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ref,
) {
  const valueChangeHandler = useCallback(
    (data: NumberFormatValues) => {
      onValueChange?.(data)
    },
    [onValueChange],
  )

  const removedNoneNumberChars = useCallback(
    (formattedValue: string): string => {
      const englishNumbers = persianNumbersToEnglish(formattedValue)
      return englishNumbers?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '')
    },
    [],
  )

  const isAllowed = useCallback((values: NumberFormatValues) => {
    const { formattedValue } = values

    // Allow empty value
    if (!formattedValue || formattedValue === '') return true

    // Don't allow values that start with multiple zeros (like "000")
    if (
      formattedValue.length > 1 &&
      formattedValue.startsWith('0') &&
      !formattedValue.includes('.')
    ) {
      return false
    }

    // For integer values, don't allow leading zeros
    if (formattedValue.match(/^0\d+/)) {
      return false
    }

    // Allow valid numbers
    return true
  }, [])
  return (
    <NumberFormat
      allowEmptyFormatting
      customInput={CustomTextField}
      data-testid='input-number'
      thousandSeparator={thousandSeparator}
      isNumericString={true}
      label={label}
      removeFormatting={removedNoneNumberChars}
      type='tel'
      onValueChange={valueChangeHandler}
      isAllowed={isAllowed}
      {...rest}
    />
  )
})
